import { Fragment, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Loader2, Info, ChevronDown, ChevronRight, CheckCircle2 } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { formatCurrency, formatPercent, formatDateLabel } from '@/lib/format';
import { EmptyState } from '@/components/EmptyState';
import { KpiCard } from '@/components/KpiCard';
import { MonthPicker } from '@/components/MonthPicker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useScopeStore, type NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';

/**
 * `/dashboard/daily-report-sales-progress` (v0.5、参考値)。Full限定。
 * 設計: docs/design/v05-daily-report-sales-progress-dashboard.md
 *
 * ⚠️ これは日報ベースの速報値であり、月末確定後の数値 (`/dashboard/targets`) とは別物。
 *    数値の性質の違いを常に画面上で明示する (§5)。
 * ⚠️ 「日報売上進捗率」(店舗のみ) を主、「参考進捗率」(前月デリバリー加算) を副として表示する。
 *    デリバリー加算は常に実績を上振れさせる構造のため、等価表示だと誤読を招く (design-expert指摘)。
 */

type ProgressMetrics = {
  daily_report_total: number;
  report_count: number;
  delivery_reference: number;
  target: number | null;
  progress_rate_store_only: number | null;
  progress_rate_with_delivery_ref: number | null;
  /** 増分3 (§11): 目標達成に必要な1日あたり平均売上。target未設定/未登録日数0なら null、達成済みは0。 */
  required_daily_store_only: number | null;
  required_daily_with_delivery_ref: number | null;
  /** 増分4 (§12): このままの日報売上ペースが続いた場合の月末着地予測。登録済み日数0なら null。 */
  forecast_month_end_store_only: number | null;
  forecast_month_end_with_delivery_ref: number | null;
};

type StoreRow = ProgressMetrics & { store_id: string; store_name: string };

/**
 * 増分5 (§13): `registered_days`/`unregistered_days` は summary のみが持つ (店舗ごとの内訳は
 * 画面上表示箇所が無いため`stores[]`には含めない、YAGNI)。userフィードバック反映により
 * 「今日を含めた暦日数」から「日報が登録済みかどうか」ベースに再定義した — 過去の未登録日
 * (日報未提出) も未来日と同様に「未登録」として扱う。
 */
type Summary = ProgressMetrics & {
  registered_days: number;
  unregistered_days: number;
};

type ProgressResponse = {
  month: string;
  as_of: string;
  field_resolved: boolean;
  /** D-951と同じ上限による打ち切り (kpi-targets.ts参照)。渡来武規模では発火しない。 */
  rollup_skipped: boolean;
  /** 売上項目が税込で、日報1件ごとに税抜へ換算した値か (docs/design/v05-consumption-tax-rate-and-target-tax-basis.md §6-5) */
  tax_converted: boolean;
  tax_conversion_error: TaxConversionError | null;
  summary: Summary;
  stores: StoreRow[];
};

type TaxConversionError = { message: string; missing_rate_dates: string[] };

/** 換算できないときは税込のまま比べず、理由と税率が無い日付を出す。 */
function TaxConversionAlert({ error }: { error: TaxConversionError }) {
  return (
    <div role="alert" className="rounded border border-danger-border bg-danger-bg px-3 py-2 text-sm text-danger-text space-y-1">
      <p>{error.message}</p>
      {error.missing_rate_dates.length > 0 && (
        <p className="text-xs">
          税率が無い日付: {error.missing_rate_dates.map((d) => formatDateLabel(d, { withWeekday: true })).join('、')}
        </p>
      )}
      <p className="text-xs">
        <Link to="/settings/consumption-tax-rates" className="underline">消費税率の設定</Link>
        {' '}または日報項目の設定を確認してください。
      </p>
    </div>
  );
}

/**
 * 増分3 (§11): 「目標達成に必要な1日あたり平均売上」の表示。進捗率が既に100%以上なら
 * 「達成済み」に色+アイコン+テキストの三重表現で置き換える (Design実装後レビューHIGH/MEDIUM反映、
 * WCAG 1.4.1、新規フィールドを増やさず既存の進捗率から導出)。
 */
function RequiredDailyValue({ requiredDaily, progressRate, iconSize = 12 }: {
  requiredDaily: number | null; progressRate: number | null; iconSize?: number;
}) {
  if (progressRate !== null && progressRate >= 1) {
    return (
      <span className="inline-flex items-center gap-1 text-success-text">
        <CheckCircle2 size={iconSize} aria-hidden="true" />
        達成済み
      </span>
    );
  }
  return <span className="tabular-nums">{formatCurrency(requiredDaily)}</span>;
}

/**
 * 増分3 (§11): `KpiCard` と同じ視覚的重み (L1エレベーション・タイポグラフィ) の
 * 「必要売上/日」カード。Design実装後レビューHIGH「既存KPIカードより明確に弱い」反映のため、
 * `KpiCard` の視覚構造 (タイトル12px/text-sub → 値28px/extrabold/tabular → 補足11px/text-sub) を踏襲する。
 * `KpiCard` 自体はフォーマット済み数値のみを扱う設計のため、「達成済み」テキスト表示を
 * 素直に混ぜられず、専用コンポーネントとして分離する。
 */
function PaceCard({ title, requiredDaily, progressRate, unregisteredDays, sourceBadge }: {
  title: string; requiredDaily: number | null; progressRate: number | null; unregisteredDays: number; sourceBadge?: string;
}) {
  return (
    <div className="relative overflow-hidden bg-surface-card rounded-lg shadow-card min-h-[128px] p-4 flex flex-col justify-between">
      <div className="flex items-center justify-between gap-1">
        <span className="text-[12px] font-bold text-text-sub tracking-wide truncate" title={title}>{title}</span>
        {sourceBadge && (
          <span className="shrink-0 text-[10px] font-semibold text-text-sub bg-surface-sunken rounded-full px-1.5 py-0.5 whitespace-nowrap">
            {sourceBadge}
          </span>
        )}
      </div>
      <div
        className="tabular-nums text-text-main font-extrabold leading-tight -tracking-[0.025em] mt-1"
        style={{ fontSize: 'clamp(1.375rem, 5.2vw, 1.75rem)' }}
      >
        <RequiredDailyValue requiredDaily={requiredDaily} progressRate={progressRate} iconSize={20} />
      </div>
      <div className="mt-1 text-[11px] text-text-sub">
        {unregisteredDays > 0 ? `未登録${unregisteredDays}日分（日報未提出の日を含む）` : '当月は全日登録済みです'}
      </div>
    </div>
  );
}

/**
 * 増分4 (§12): 店舗別テーブルの「着地予測」セルに使う、着地予測額+目標比バッジ。
 * `KpiCard`の`delta`バッジ (bg-success-bg/text-success-text、bg-danger-bg/text-danger-text、▲/▼、
 * gap===0は中立のbg-surface-sunken/text-text-sub/±) と同じ配色・記号・境界値の扱いに揃え、
 * カードとテーブルで視覚言語を統一する (WCAG 1.4.1、色+アイコン+テキスト)。
 * ⚠️ 実装後レビューDesign/design-and-code両者が独立に検出: gap===0(予測が目標と完全一致)を
 * `gap>=0`で緑扱いしていたのはKpiCardのcomputeDelta/badgeClsの中立扱いと矛盾していた。
 */
function ForecastCell({ forecast, target }: { forecast: number | null; target: number | null }) {
  if (forecast === null) return <span className="text-text-sub">−</span>;
  const gap = target !== null ? forecast - target : null;
  const badgeCls = gap === null ? null
    : gap === 0 ? 'bg-surface-sunken text-text-sub'
    : gap > 0 ? 'bg-success-bg text-success-text'
    : 'bg-danger-bg text-danger-text';
  const arrow = gap === null || gap === 0 ? '±' : gap > 0 ? '▲' : '▼';
  return (
    <div>
      <div className="tabular-nums">{formatCurrency(forecast)}</div>
      {gap !== null && (
        <div className={`inline-flex items-center gap-0.5 text-[11px] font-extrabold tabular-nums px-2 py-0.5 rounded-sm ${badgeCls}`}>
          <span aria-hidden="true">{arrow}</span>
          {formatCurrency(Math.abs(gap))}
        </div>
      )}
    </div>
  );
}

type DailyBreakdownDay = {
  date: string;
  total: number;
  report_count: number;
  status: 'registered' | 'unregistered' | 'future';
};

type DailyBreakdownResponse = {
  month: string;
  as_of: string;
  field_resolved: boolean;
  tax_converted: boolean;
  tax_conversion_error: TaxConversionError | null;
  days: DailyBreakdownDay[];
};

const currentMonth = (): string => `${new Date().toISOString().slice(0, 7)}-01`;

const STICKY_COL = 'sticky left-0 z-10 bg-surface-card border-r border-surface-line';

export function DailyReportSalesProgressPage() {
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  const globalSelectedNodeId = useScopeStore((s) => s.selectedNodeId);
  const [nodeId, setNodeId] = useState('');
  useEffect(() => {
    if (!nodeId && globalSelectedNodeId) setNodeId(globalSelectedNodeId);
  }, [globalSelectedNodeId]);
  const effectiveNodeId = nodeId || flatNodes[0]?.node.node_id || '';
  const [month, setMonth] = useState(currentMonth());

  const { data, isLoading, isError } = useQuery<ProgressResponse>({
    queryKey: ['daily-report-sales-progress', effectiveNodeId, month],
    queryFn: () => apiFetch<ProgressResponse>(
      `/daily-report-sales-progress?node_id=${encodeURIComponent(effectiveNodeId)}&month=${encodeURIComponent(month)}`),
    enabled: !!effectiveNodeId,
  });

  if (!nodesData) {
    return <div className="p-4 flex items-center gap-2 text-text-sub"><Loader2 className="animate-spin" size={16} />読み込み中…</div>;
  }
  if (!effectiveNodeId) {
    return <div className="p-4"><EmptyState message="組織が見つかりません" description="組織階層が設定されていません。" /></div>;
  }

  return (
    <div className="p-4 space-y-4">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-text-main">日報売上の進捗（参考値）</h1>
        <p className="text-sm text-text-sub">
          日報で入力された店舗売上をもとに、当月の売上目標に対する進捗を店舗別・合算で確認します。
        </p>
      </header>

      <p className="text-sm text-alert-text bg-alert-bg border border-alert-border rounded px-3 py-2">
        これは日報ベースの参考値（速報）です。月末確定後の数値は「目標と実績」ページで確認してください。
        数値が異なる場合があります。
      </p>

      <div className="flex flex-wrap gap-3">
        <div className="min-w-0 w-full sm:w-72">
          <label className="block text-xs text-text-sub mb-1" htmlFor="drsp-node">対象組織</label>
          <Select value={effectiveNodeId} onValueChange={setNodeId}>
            <SelectTrigger id="drsp-node" className="w-full min-h-[44px]"><SelectValue placeholder="組織を選ぶ" /></SelectTrigger>
            <SelectContent>
              {flatNodes.map(({ node, depth }) => (
                <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-0 w-full sm:w-56">
          <label className="block text-xs text-text-sub mb-1" htmlFor="drsp-month">対象月</label>
          <div className="flex items-center gap-2">
            <MonthPicker id="drsp-month" value={month} onChange={(v) => setMonth(`${v}-01`)} className="flex-1" aria-label="対象月" />
            {month !== currentMonth() && (
              <button
                type="button"
                onClick={() => setMonth(currentMonth())}
                className="shrink-0 min-h-[44px] px-3 text-xs font-medium text-text-sub bg-surface-sunken rounded whitespace-nowrap"
              >
                今月
              </button>
            )}
          </div>
        </div>
      </div>

      {isLoading && <div className="flex items-center gap-2 text-text-sub"><Loader2 className="animate-spin" size={16} />読み込み中…</div>}
      {isError && <p className="text-sm text-danger-text">進捗の取得に失敗しました。時間をおいて再度お試しください。</p>}

      {data && data.rollup_skipped && (
        <EmptyState
          message="配下の組織が多いため、進捗を計算していません"
          description="下の「対象組織」からブランドや店舗を選び直してください。"
        />
      )}

      {data && !data.rollup_skipped && !data.field_resolved && (
        <EmptyState
          message="日報の売上項目が見つかりません"
          description="このテナントには売上を記録する日報項目が設定されていません。管理者に確認してください。"
        />
      )}

      {data && !data.rollup_skipped && data.field_resolved && data.tax_conversion_error && (
        <TaxConversionAlert error={data.tax_conversion_error} />
      )}

      {data && !data.rollup_skipped && data.field_resolved && !data.tax_conversion_error && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <KpiCard
                title="日報売上進捗率"
                value={data.summary.progress_rate_store_only}
                format="percent"
                footnote={`日報売上累計${data.tax_converted ? '（税抜換算）' : ''} ${formatCurrency(data.summary.daily_report_total)} / 目標 ${formatCurrency(data.summary.target)}`}
              />
            </div>
            <div className="md:col-span-1">
              <KpiCard
                title="参考進捗率（前月デリバリー加算）"
                value={data.summary.progress_rate_with_delivery_ref}
                format="percent"
                sourceBadge="参考値"
                footnote={`前月デリバリー実績 ${formatCurrency(data.summary.delivery_reference)} を加算`}
              />
            </div>
          </div>

          {/* 増分4 Design実装後レビューMEDIUM反映: 診断(進捗率)→予測(着地見込み)→処方箋(必要売上/日)の
              自然な物語順序に合わせ、着地予測を必要売上/日より先に表示する */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <KpiCard
                title="月末着地予測（日報基準）"
                value={data.summary.forecast_month_end_store_only}
                format="currency"
                delta={{ prevValue: data.summary.target, label: '目標比', invert: false }}
                footnote={data.summary.registered_days > 0
                  ? `登録済み${data.summary.registered_days}日分の実績から算出`
                  : '実績不足のため算出できません'}
              />
            </div>
            <div className="md:col-span-1">
              <KpiCard
                title="月末着地予測（参考基準・前月デリバリー加算）"
                value={data.summary.forecast_month_end_with_delivery_ref}
                format="currency"
                sourceBadge="参考値"
                delta={{ prevValue: data.summary.target, label: '目標比', invert: false }}
                footnote={data.summary.registered_days > 0
                  ? `前月デリバリー実績 ${formatCurrency(data.summary.delivery_reference)} を加算し、登録済み${data.summary.registered_days}日分の実績から算出`
                  : '算出できません'}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <PaceCard
                title="必要売上/日（日報基準）"
                requiredDaily={data.summary.required_daily_store_only}
                progressRate={data.summary.progress_rate_store_only}
                unregisteredDays={data.summary.unregistered_days}
              />
            </div>
            <div className="md:col-span-1">
              <PaceCard
                title="必要売上/日（参考基準・前月デリバリー加算）"
                requiredDaily={data.summary.required_daily_with_delivery_ref}
                progressRate={data.summary.progress_rate_with_delivery_ref}
                unregisteredDays={data.summary.unregistered_days}
                sourceBadge="参考値"
              />
            </div>
          </div>

          <StoreTable stores={data.stores} month={month} taxConverted={data.tax_converted} />
        </>
      )}
    </div>
  );
}

const STORE_TABLE_COLS = 10;

function StoreTable({ stores, month, taxConverted }: { stores: StoreRow[]; month: string; taxConverted: boolean }) {
  const [expandedStoreId, setExpandedStoreId] = useState<string | null>(null);
  const toggleStore = (storeId: string) => setExpandedStoreId((cur) => (cur === storeId ? null : storeId));

  if (stores.length === 0) {
    return <EmptyState message="対象の店舗がありません" description="選択した組織の配下に店舗が見つかりませんでした。" />;
  }
  return (
    <section className="rounded border border-surface-border bg-surface-card p-4 space-y-2">
      <h2 className="text-sm font-semibold text-text-main">店舗別</h2>
      <p className="text-xs text-text-sub">店舗名をクリックすると、日別の内訳を確認できます。</p>
      <div className="overflow-x-auto">
        <table className="relative min-w-full text-sm">
          <caption className="sr-only">店舗ごとの日報売上進捗</caption>
          <thead>
            <tr className="text-left text-text-sub border-b border-surface-border">
              <th scope="col" className={`${STICKY_COL} py-2 pr-4 font-medium whitespace-nowrap`}>店舗</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">日報売上累計{taxConverted && '（税抜換算）'}</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">日報件数</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">目標</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">進捗率</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">参考進捗率</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">月末着地予測（日報基準）</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">月末着地予測（参考基準）</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">必要売上/日（日報基準）</th>
              <th scope="col" className="relative py-2 font-medium whitespace-nowrap text-right">必要売上/日（参考基準）</th>
            </tr>
          </thead>
          <tbody>
            {stores.map((s) => {
              const expanded = expandedStoreId === s.store_id;
              return (
                <Fragment key={s.store_id}>
                  <tr
                    tabIndex={0}
                    role="button"
                    aria-expanded={expanded}
                    className="border-b border-surface-line hover:bg-surface-sunken cursor-pointer focus-ring"
                    onClick={() => toggleStore(s.store_id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleStore(s.store_id); } }}
                  >
                    <td className={`${STICKY_COL} py-2 pr-4 whitespace-nowrap`}>
                      <span className="inline-flex items-center gap-1">
                        {expanded ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
                        {s.store_name}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap">{formatCurrency(s.daily_report_total)}</td>
                    <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap">
                      {s.report_count === 0
                        ? <span className="inline-flex items-center gap-0.5 text-xs text-text-sub"><Info size={12} aria-hidden="true" />未提出</span>
                        : `${s.report_count}件`}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap">{formatCurrency(s.target)}</td>
                    <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap">{formatPercent(s.progress_rate_store_only)}</td>
                    <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap text-text-sub">{formatPercent(s.progress_rate_with_delivery_ref)}</td>
                    <td className="py-2 pr-4 text-right whitespace-nowrap">
                      <ForecastCell forecast={s.forecast_month_end_store_only} target={s.target} />
                    </td>
                    <td className="py-2 pr-4 text-right whitespace-nowrap">
                      <ForecastCell forecast={s.forecast_month_end_with_delivery_ref} target={s.target} />
                    </td>
                    <td className="py-2 pr-4 text-right whitespace-nowrap">
                      <RequiredDailyValue requiredDaily={s.required_daily_store_only} progressRate={s.progress_rate_store_only} />
                    </td>
                    <td className="py-2 text-right whitespace-nowrap text-text-sub">
                      <RequiredDailyValue requiredDaily={s.required_daily_with_delivery_ref} progressRate={s.progress_rate_with_delivery_ref} />
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="border-b border-surface-line">
                      <td colSpan={STORE_TABLE_COLS} className="bg-surface-sunken p-0">
                        <DailyBreakdownPanel storeId={s.store_id} month={month} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DailyBreakdownPanel({ storeId, month }: { storeId: string; month: string }) {
  const { data, isLoading, isError, refetch } = useQuery<DailyBreakdownResponse>({
    queryKey: ['daily-report-sales-progress-breakdown', storeId, month],
    queryFn: () => apiFetch<DailyBreakdownResponse>(
      `/daily-report-sales-progress/daily-breakdown?node_id=${encodeURIComponent(storeId)}&month=${encodeURIComponent(month)}`),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 text-sm text-text-sub">
        <Loader2 className="animate-spin" size={14} aria-hidden="true" />読み込み中…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="flex items-center justify-between gap-2 px-4 py-3 text-sm text-danger-text">
        <span>日別内訳の取得に失敗しました。</span>
        <button
          type="button"
          onClick={() => refetch()}
          className="shrink-0 min-h-[32px] px-2 text-xs font-medium text-text-sub bg-surface-card rounded border border-surface-border"
        >
          再試行
        </button>
      </div>
    );
  }

  if (data.tax_conversion_error) {
    return <div className="px-4 py-3"><TaxConversionAlert error={data.tax_conversion_error} /></div>;
  }

  const futureCount = data.days.filter((d) => d.status === 'future').length;
  const unregisteredTotal = data.days.filter((d) => d.status === 'unregistered' || d.status === 'future').length;

  return (
    <div className="px-4 py-3">
      {/* 増分5 (§13) レビューHIGH反映: カードの「未登録N日分」は未来日を含む合計のため、
          展開パネルの「未登録」「未来日」ラベル別カウントと数値が食い違って見える。
          同じ数値に突き合わせられるよう小計を明示する。 */}
      <p className="mb-2 text-xs text-text-sub">
        未登録合計 {unregisteredTotal}日分（うち未来日 {futureCount}日）
      </p>
      <table className="w-full text-sm">
        <caption className="sr-only">日別の日報売上内訳</caption>
        <thead>
          <tr className="text-left text-text-sub border-b border-surface-line">
            <th scope="col" className="py-1 pr-4 font-medium whitespace-nowrap">日付</th>
            <th scope="col" className="py-1 pr-4 font-medium whitespace-nowrap text-right">金額{data.tax_converted && '（税抜換算）'}</th>
            <th scope="col" className="py-1 font-medium whitespace-nowrap text-right">状態</th>
          </tr>
        </thead>
        <tbody>
          {data.days.map((d) => (
            <tr key={d.date} className="border-b border-surface-line last:border-b-0">
              <td className="py-1 pr-4 whitespace-nowrap tabular-nums">{formatDateLabel(d.date, { withWeekday: true })}</td>
              <td className={`py-1 pr-4 text-right tabular-nums whitespace-nowrap ${d.status === 'registered' ? 'text-text-main' : 'text-text-sub'}`}>
                {d.status === 'registered' ? formatCurrency(d.total) : '―'}
              </td>
              <td className="py-1 text-right whitespace-nowrap text-text-sub">
                {d.status === 'unregistered' ? '未登録' : d.status === 'future' ? '未来日' : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
